import { msg } from "@lingui/core/macro";
import {
  createReservationMutation,
  getReservationsQuery,
  getTablesQuery,
  queryKeys,
  type ReservationKind,
  seatReservationMutation,
  setReservationStatusMutation,
} from "@comandero/shared";
import type { Branch, DiningTable, Organization, Reservation } from "@comandero/powersync";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import { createSignal, For, Show } from "solid-js";
import { Button } from "@/components/ui/button";
import { Select, toSelectOptions } from "@/components/ui/select";
import { TextField } from "@/components/ui/text-field";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";

interface ReservationsManagerProps {
  org: () => Organization | null;
  branch: () => Branch | null;
}

export function ReservationsManager(props: ReservationsManagerProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();

  const [name, setName] = createSignal("");
  const [phone, setPhone] = createSignal("");
  const [party, setParty] = createSignal("2");
  const [datetime, setDatetime] = createSignal("");
  const [kind, setKind] = createSignal<ReservationKind>("reservation");
  const [tableId, setTableId] = createSignal("");

  const orgId = (): string | null => props.org()?.id ?? null;
  const branchId = (): string | null => props.branch()?.id ?? null;

  const reservations = createPowerSyncQuery<Reservation>({
    queryKey: () => queryKeys.comandero.reservations(branchId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const bid = branchId();
      return d && bid ? getReservationsQuery(d, bid) : null;
    },
    db,
    enabled: () => Boolean(branchId()),
  });

  const tables = createPowerSyncQuery<DiningTable>({
    queryKey: () => queryKeys.comandero.tables(branchId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      const bid = branchId();
      return d && id && bid ? getTablesQuery(d, id, bid) : null;
    },
    db,
    enabled: () => Boolean(branchId()),
  });

  async function addReservation(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const id = orgId();
    const bid = branchId();
    if (!d || !id || !bid || !name().trim()) {
      return;
    }
    // datetime-local → ISO; waitlist no lleva hora comprometida.
    const reservedAt =
      kind() === "reservation" && datetime() ? new Date(datetime()).toISOString() : null;
    await createReservationMutation(d, id, bid, name(), {
      phone: phone() || undefined,
      partySize: Number(party() || "2"),
      reservedAt,
      kind: kind(),
      tableId: tableId() || null,
    });
    setName("");
    setPhone("");
    setDatetime("");
    setTableId("");
  }

  async function seat(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await seatReservationMutation(d, id);
  }

  async function markNoShow(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await setReservationStatusMutation(d, id, "no_show");
  }

  return (
    <div class="flex flex-col gap-6">
      <section>
        <h2 class="mb-3 text-lg font-semibold">{t(msg`New reservation`)}</h2>
        <form class="mb-3 flex flex-wrap gap-2" onSubmit={addReservation}>
          <TextField
            data-testid="reservation-name-input"
            placeholder={t(msg`Guest name`)}
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
          />
          <TextField
            data-testid="reservation-phone-input"
            placeholder={t(msg`Phone`)}
            inputmode="tel"
            value={phone()}
            onInput={(e) => setPhone(e.currentTarget.value)}
          />
          <TextField
            data-testid="reservation-party-input"
            placeholder={t(msg`Guests`)}
            inputmode="numeric"
            value={party()}
            onInput={(e) => setParty(e.currentTarget.value)}
          />
          <Select
            data-testid="reservation-kind-select"
            aria-label={t(msg`Type`)}
            value={kind()}
            onChange={(v) => setKind(v === "waitlist" ? "waitlist" : "reservation")}
            options={[
              { value: "reservation", label: t(msg`Reservation`) },
              { value: "waitlist", label: t(msg`Waitlist`) },
            ]}
          />
          <Show when={kind() === "reservation"}>
            <input
              data-testid="reservation-datetime-input"
              type="datetime-local"
              class="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={datetime()}
              onInput={(e) => setDatetime(e.currentTarget.value)}
            />
          </Show>
          <Select
            data-testid="reservation-table-select"
            aria-label={t(msg`Table`)}
            value={tableId()}
            onChange={setTableId}
            options={[
              { value: "", label: t(msg`No table`) },
              ...toSelectOptions(
                tables.data,
                (tbl) => tbl.id,
                (tbl) => tbl.name,
              ),
            ]}
          />
          <Button data-testid="add-reservation-btn" type="submit">
            {t(msg`Add`)}
          </Button>
        </form>
      </section>

      <section>
        <h2 class="mb-3 text-lg font-semibold">{t(msg`Reservations & waitlist`)}</h2>
        <ul class="flex flex-col gap-2">
          <For each={reservations.data}>
            {(r) => (
              <li
                class="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/40 px-3 py-2 text-sm"
                data-testid="reservation-item"
                data-reservation-id={r.id}
                data-reservation-status={r.status}
              >
                <span data-testid="reservation-row-name">{r.customer_name}</span>
                <span class="text-muted-foreground">
                  {r.party_size} {t(msg`guests`)} · {r.kind}
                </span>
                <span class="text-muted-foreground" data-testid="reservation-row-status">
                  {r.status}
                </span>
                <div class="flex gap-2">
                  <Show when={r.status === "reserved"}>
                    <Button
                      size="sm"
                      variant="secondary"
                      data-testid="reservation-seat-btn"
                      onClick={() => void seat(r.id)}
                    >
                      {t(msg`Seat`)}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid="reservation-noshow-btn"
                      onClick={() => void markNoShow(r.id)}
                    >
                      {t(msg`No-show`)}
                    </Button>
                  </Show>
                </div>
              </li>
            )}
          </For>
        </ul>
      </section>
    </div>
  );
}
